import { useCallback, useEffect, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { CalendarHeart, Pencil, Plus, X } from "lucide-react";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const EMPTY = { key: "", name: "", title: "", body: "", month: "", day: "", credits: 0 };

export default function AdminFestivita() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // {key, name, title, body, month, day, credits}
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/festivita").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  const save = async (payload) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/admin/festivita", payload);
      toast.success("Ricorrenza salvata.");
      setEditing(null);
      setAdding(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const dateLabel = (f) => f.easter_offset !== undefined
    ? `Calcolata ogni anno (Pasqua ${f.easter_offset === 0 ? "" : "+1 giorno"})`
    : `${f.day} ${MESI[(f.month || 1) - 1]}`;

  return (
    <div data-testid="admin-festivita-section">
      <h2 className="font-display text-2xl text-amber-100 mb-2">Festività e auguri automatici</h2>
      <p className="text-xs text-purple-300/60 mb-6 leading-relaxed">
        Auguri inviati automaticamente a nome di Arcana. I crediti regalo spettano solo a compleanno e Natale.
        Puoi modificare i testi o aggiungere nuove ricorrenze a data fissa.
      </p>
      {rows === null ? null : (
        <div className="space-y-3" data-testid="admin-festivita-list">
          {rows.map((f) => (
            <div key={f.key} className="mystic-card p-5" data-testid={`festivita-${f.key}`}>
              {editing === f.key ? (
                <div className="space-y-3">
                  <input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                    className="input-dark w-full" placeholder="Nome ricorrenza" data-testid={`festivita-edit-name-${f.key}`} />
                  <input value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                    className="input-dark w-full" placeholder="Titolo notifica" data-testid={`festivita-edit-title-${f.key}`} />
                  <textarea value={form.body} onChange={(e) => setForm((s) => ({ ...s, body: e.target.value }))}
                    rows={3} className="input-dark w-full resize-y" placeholder="Testo auguri" data-testid={`festivita-edit-body-${f.key}`} />
                  <div className="flex gap-2">
                    <button onClick={() => save({ key: f.key, name: form.name, title: form.title, body: form.body, month: f.month, day: f.day, credits: f.credits ?? 0 })}
                      disabled={busy} data-testid={`festivita-save-${f.key}`}
                      className="text-xs text-amber-200 border border-amber-500/40 rounded-full px-4 py-1.5 hover:bg-amber-500/10 transition-colors disabled:opacity-50">
                      Salva
                    </button>
                    <button onClick={() => setEditing(null)} data-testid={`festivita-cancel-${f.key}`}
                      className="text-xs text-purple-300/60 hover:text-amber-300 transition-colors inline-flex items-center gap-1">
                      <X className="w-3.5 h-3.5" /> Annulla
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
                    <CalendarHeart className="w-4 h-4 text-amber-400/70" />
                    <span className="text-sm text-amber-100 font-medium">{f.name}</span>
                    <span className="text-[11px] text-purple-300/50">{dateLabel(f)}</span>
                    <span className={`ml-auto text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${f.credits ? "border-amber-500/50 text-amber-300" : "border-purple-500/40 text-purple-300/50"}`}>
                      {f.credits ? `+${f.credits} crediti` : "solo auguri"}
                    </span>
                  </div>
                  <p className="text-xs text-amber-200/80">{f.title}</p>
                  <p className="text-xs text-purple-200/60 mt-1 leading-relaxed">{f.body}</p>
                  <button onClick={() => { setEditing(f.key); setForm({ key: f.key, name: f.name, title: f.title, body: f.body, month: f.month || "", day: f.day || "", credits: f.credits ?? 0 }); }}
                    data-testid={`festivita-edit-${f.key}`}
                    className="mt-3 text-xs text-purple-200 border border-[#4a2175] rounded-full px-4 py-1.5 hover:bg-purple-500/10 transition-colors inline-flex items-center gap-1.5">
                    <Pencil className="w-3.5 h-3.5" /> Modifica testi
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="mt-8">
        {adding ? (
          <div className="mystic-card p-5 space-y-3" data-testid="festivita-add-form">
            <p className="text-sm text-amber-100">Nuova ricorrenza a data fissa</p>
            <input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value, key: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") }))}
              className="input-dark w-full" placeholder="Nome ricorrenza" data-testid="festivita-add-name" />
            <div className="grid grid-cols-2 gap-2">
              <select value={form.day} onChange={(e) => setForm((s) => ({ ...s, day: parseInt(e.target.value) || "" }))} className="input-dark" data-testid="festivita-add-day">
                <option value="">Giorno</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={form.month} onChange={(e) => setForm((s) => ({ ...s, month: parseInt(e.target.value) || "" }))} className="input-dark" data-testid="festivita-add-month">
                <option value="">Mese</option>
                {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <input value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
              className="input-dark w-full" placeholder="Titolo notifica" data-testid="festivita-add-title" />
            <textarea value={form.body} onChange={(e) => setForm((s) => ({ ...s, body: e.target.value }))}
              rows={3} className="input-dark w-full resize-y" placeholder="Testo auguri" data-testid="festivita-add-body" />
            <div className="flex gap-2">
              <button onClick={() => save({ ...form, month: form.month || null, day: form.day || null, credits: 0 })}
                disabled={busy || !form.key || !form.name || !form.title || !form.body || !form.month || !form.day}
                data-testid="festivita-add-save"
                className="text-xs text-amber-200 border border-amber-500/40 rounded-full px-4 py-1.5 hover:bg-amber-500/10 transition-colors disabled:opacity-50">
                Aggiungi ricorrenza
              </button>
              <button onClick={() => { setAdding(false); setForm(EMPTY); }} data-testid="festivita-add-cancel"
                className="text-xs text-purple-300/60 hover:text-amber-300 transition-colors">
                Annulla
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} data-testid="festivita-add-open"
            className="text-xs text-amber-200 border border-amber-500/40 rounded-full px-4 py-2 hover:bg-amber-500/10 transition-colors inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Aggiungi ricorrenza
          </button>
        )}
      </div>
    </div>
  );
}
